import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { CurrencyDollar, CheckCircle, User } from "@phosphor-icons/react";
import { formatBRL, formatDate } from "@/lib/format";

const ClientPayments = () => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/client/payments").then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  const total = items.reduce((acc, p) => acc + Number(p.amount || 0), 0);

  // Group by user_id for clarity
  const groups = useMemo(() => {
    const map = new Map();
    items.forEach((p) => {
      const key = p.user_id;
      const g = map.get(key) || { user_id: key, user_name: p.user_name, username: p.username, beneficiary: p.beneficiary, items: [], total: 0 };
      g.items.push(p);
      g.total += Number(p.amount || 0);
      map.set(key, g);
    });
    return Array.from(map.values());
  }, [items]);

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs tracking-[0.3em] uppercase text-zinc-500 font-semibold">Histórico</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Pagamentos</h1>
      </div>

      <Card className="border-zinc-200 rounded-2xl p-6 card-soft">
        <div className="text-[11px] tracking-widest uppercase text-zinc-500 font-semibold">Total pago</div>
        <div className="metric-num text-4xl text-emerald-700 mt-2">{formatBRL(total)}</div>
        <div className="text-sm text-zinc-500 mt-1">
          {items.length} pagamento{items.length !== 1 && "s"} em {groups.length} plano{groups.length !== 1 && "s"}
        </div>
      </Card>

      {items.length === 0 ? (
        <Card className="border-zinc-200 rounded-2xl p-10 text-center text-zinc-500 card-soft">
          <CurrencyDollar size={28} weight="duotone" className="mx-auto mb-2 text-zinc-400"/>
          Nenhum pagamento registrado ainda.
        </Card>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <Card key={g.user_id} className="border-zinc-200 rounded-2xl p-0 overflow-hidden card-soft" data-testid={`pay-group-${g.user_id}`}>
              <div className="bg-zinc-50/80 border-b border-zinc-200 px-5 py-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-zinc-900 text-amber-300 grid place-items-center text-sm font-semibold">
                  {g.beneficiary ? <User size={16} weight="duotone"/> : (g.user_name || "?").slice(0,1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">
                    {g.user_name}
                    {g.beneficiary && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-widest font-semibold bg-zinc-100 border border-zinc-200 text-zinc-600">{g.beneficiary}</span>}
                  </div>
                  {g.username && <div className="text-[11px] text-zinc-500">@{g.username}</div>}
                </div>
                <div className="text-right">
                  <div className="text-[10px] tracking-widest uppercase text-zinc-500">Total</div>
                  <div className="font-display text-base font-semibold tab-nums">{formatBRL(g.total)}</div>
                </div>
              </div>
              <ul className="divide-y divide-zinc-100">
                {g.items.map((p) => (
                  <li key={p.id} className="px-5 py-3 flex items-center gap-4" data-testid={`payment-${p.id}`}>
                    <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center flex-shrink-0">
                      <CheckCircle size={16} weight="fill"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium tab-nums">{formatBRL(p.amount)}</div>
                      <div className="text-[11px] text-zinc-500">
                        {formatDate(p.paid_at)} · <span className="capitalize">{p.method}</span>
                        {p.discount_pct ? ` · Desconto ${p.discount_pct}%` : ""}
                        {p.batch_id ? " · pago em lote" : ""}
                      </div>
                    </div>
                    {p.reference_period && (
                      <div className="text-[11px] text-zinc-500 whitespace-nowrap">{p.reference_period}</div>
                    )}
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientPayments;
